import React, { useState, useEffect, ErrorInfo, ReactNode } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SecureLogin } from './components/SecureLogin';
import { ClockItDashboard } from './components/ClockItDashboard';
import { ClockItBottomNav } from './components/ClockItBottomNav';
import { ClockItClockInOut } from './components/ClockItClockInOut';
import { ClockItRights } from './components/ClockItRights';
import { NewCalendar as Calendar } from './components/NewCalendar';
import { Onboarding } from './components/Onboarding';
import { AuthCallback } from './components/AuthCallback';
import { ResetPassword } from './components/ResetPassword';
import { UserStats } from './components/UserStats';
import { SuperuserSetup } from './components/SuperuserSetup';
import { AdminHub } from './components/AdminHub';
import { BulkApproval } from './components/BulkApproval';
import { ScraperControlDashboard } from './components/ScraperControlDashboard';
import { ScraperMonitoringDashboard } from './components/ScraperMonitoringDashboard';
import { ImportSchedule } from './components/ImportSchedule';
import { EmailVerificationRequired } from './components/EmailVerificationRequired';
import { ProfileSettings } from './components/ProfileSettings';
import { ImmediateAuthCheck } from './utils/immediateAuthCheck';
import { AuthCache } from './utils/authCache';
import { shouldShowGracePeriodBlocker } from './utils/gracePeriodCheck';
import './index.css';

type ScreenType = 
  | 'dashboard' 
  | 'clock'
  | 'calendar'
  | 'rights'
  | 'stats'
  | 'profile'
  | 'onboarding' 
  | 'analytics' 
  | 'timeblock-manager'
  | 'reports'
  | 'users'
  | 'admin'
  | 'bulk-approval'
  | 'scraper-control'
  | 'scraper-monitoring'
  | 'import-schedule'
  | 'superuser-setup'
  | 'settings'
  | 'team'
  | 'education'
  | 'time-tracking';

type Theme = 'light' | 'dark';

/**
 * Loading Screen Component
 */
function LoadingScreen({ message }: { message?: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">Loading Clock-It...</p>
        <p className="text-gray-500 text-sm mt-2">
          {message || 'Initializing secure authentication...'}
        </p>
        <div className="mt-4">
          <div className="w-48 h-1 bg-gray-200 rounded mx-auto">
            <div className="h-1 bg-blue-600 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error Boundary Component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Clock-It Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <div className="max-w-md mx-auto text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">Application Error</h2>
            <p className="text-red-600 mb-4">Something went wrong. Please refresh the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="text-red-700 cursor-pointer">Error Details</summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Main Application Content - Screen Router
 */
function AppContent() {
  const { 
    user, 
    session, 
    isAuthenticated,
    isLoading, 
    authInitialized,
    hasAdminAccess,
    hasSuperAdminAccess,
    signOut,
    resendConfirmation
  } = useAuth();
  
  const [currentScreen, setCurrentScreen] = useState<ScreenType>('dashboard');
  const [theme, setTheme] = useState<Theme>('light');
  const [selectedReportDate, setSelectedReportDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  // Check if this is an auth callback (OAuth tokens in hash or search params)
  const isAuthCallback = window.location.hash.includes('access_token') ||
                         window.location.hash.includes('refresh_token') ||
                         window.location.search.includes('code=') ||
                         window.location.pathname === '/auth/callback';

  // Check if this is a password reset link
  const isPasswordReset = (window.location.hash.includes('type=recovery') && window.location.hash.includes('access_token')) ||
                          window.location.pathname === '/reset-password' ||
                          window.location.pathname === '/auth/reset-password';

  // Handle logout
  const handleLogout = async () => {
    await signOut();
  };

  // Handle back navigation
  const handleBack = () => {
    setCurrentScreen('dashboard');
  };

  // Helper function to check if onboarding has been completed
  const isOnboardingCompleted = () => {
    try {
      const completed = localStorage.getItem('clockit-onboarded');
      return completed === 'true';
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false;
    }
  };

  // Handle onboarding completion
  const handleCompleteOnboarding = () => {
    localStorage.setItem('clockit-onboarded', 'true');
    setCurrentScreen('dashboard');
  };

  // Handle auth callback
  const handleAuthComplete = (isNewUser: boolean) => {
    // Clear the callback URL
    window.history.replaceState({}, document.title, window.location.pathname);
    
    if (isNewUser && !isOnboardingCompleted()) {
      setCurrentScreen('onboarding');
    } else {
      setCurrentScreen('dashboard');
    }
  };

  // Determine initial screen for authenticated users
  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user?.email_confirmed_at) {
        // Users without confirmed email go to onboarding if not completed
        if (!isOnboardingCompleted()) {
          setCurrentScreen('onboarding');
        } else {
          setCurrentScreen('dashboard');
        }
      } else {
        // Fully authenticated users go to dashboard
        setCurrentScreen('dashboard');
      }
    }
  }, [isAuthenticated, user?.email_confirmed_at]);

  // Handle password reset before other checks
  if (isPasswordReset) {
    return <ResetPassword onComplete={() => window.location.href = '/'} />;
  }

  // Handle auth callback before other checks
  if (isAuthCallback) {
    return <AuthCallback onAuthComplete={handleAuthComplete} />;
  }

  // Show loading while authentication is not yet initialized
  if (isLoading || !authInitialized) {
    return <LoadingScreen />;
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <SecureLogin 
        onLoginSuccess={(isNewUser?: boolean) => {
          if (isNewUser && !isOnboardingCompleted()) {
            setCurrentScreen('onboarding');
          } else {
            setCurrentScreen('dashboard');
          }
        }}
      />
    );
  }

  // GRACE PERIOD CHECK - Show blocker if grace period expired and email not verified
  if (shouldShowGracePeriodBlocker(user)) {
    return (
      <EmailVerificationRequired 
        user={user!}
        onResendEmail={async () => {
          if (user?.email) {
            await resendConfirmation(user.email);
          }
        }}
        onSignOut={signOut}
      />
    );
  }

  // Main app content for users with valid access
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Router based on current screen */}
      {currentScreen === 'onboarding' && (
        <Onboarding 
          onComplete={handleCompleteOnboarding}
        />
      )}

      {currentScreen === 'stats' && (
        <UserStats onNavigate={setCurrentScreen} />
      )}

      {currentScreen === 'superuser-setup' && (
        <SuperuserSetup />
      )}

      {/* Admin Screens - Protected by role checks */}
      {currentScreen === 'admin' && (
        <AdminHub onNavigate={setCurrentScreen} />
      )}

      {currentScreen === 'bulk-approval' && (
        hasAdminAccess ? (
          <BulkApproval onClose={() => setCurrentScreen('admin')} />
        ) : (
          <ClockItDashboard
            user={user}
            onNavigate={(screen: any) => setCurrentScreen(screen)}
            onNavigateToProfile={() => setCurrentScreen('profile')}
          />
        )
      )}

      {currentScreen === 'scraper-control' && (
        hasSuperAdminAccess ? (
          <ScraperControlDashboard />
        ) : (
          <ClockItDashboard
            user={user}
            onNavigate={(screen: any) => setCurrentScreen(screen)}
            onNavigateToProfile={() => setCurrentScreen('profile')}
          />
        )
      )}

      {currentScreen === 'scraper-monitoring' && (
        hasSuperAdminAccess ? (
          <ScraperMonitoringDashboard />
        ) : (
          <ClockItDashboard
            user={user}
            onNavigate={(screen: any) => setCurrentScreen(screen)}
            onNavigateToProfile={() => setCurrentScreen('profile')}
          />
        )
      )}

      {currentScreen === 'import-schedule' && (
        hasSuperAdminAccess ? (
          <ImportSchedule 
            onBack={() => setCurrentScreen('admin')}
            onScheduleImported={() => {
              setCurrentScreen('admin');
            }}
          />
        ) : (
          <ClockItDashboard
            user={user}
            onNavigate={(screen: any) => setCurrentScreen(screen)}
            onNavigateToProfile={() => setCurrentScreen('profile')}
          />
        )
      )}

      {currentScreen === 'clock' && (
        <ClockItClockInOut />
      )}

      {currentScreen === 'rights' && (
        <ClockItRights />
      )}

      {currentScreen === 'calendar' && (
        <Calendar />
      )}

      {currentScreen === 'profile' && (
        <ProfileSettings
          onLogout={handleLogout}
          onBack={() => setCurrentScreen('dashboard')}
        />
      )}

      {/* Default Dashboard */}
      {currentScreen === 'dashboard' && (
        <ClockItDashboard
          user={user}
          onNavigate={(screen: any) => setCurrentScreen(screen)}
          onNavigateToProfile={() => setCurrentScreen('profile')}
        />
      )}

      {/* Bottom Navigation - Show for all main app screens */}
      {!['onboarding'].includes(currentScreen) && (
        <ClockItBottomNav
          currentScreen={currentScreen}
          onScreenChange={setCurrentScreen}
        />
      )}
    </div>
  );
}

/**
 * Root App Component
 */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}